import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ModalController } from '@ionic/angular';
import { MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { SeriesOption } from 'echarts';
import { LegendOption } from 'echarts/types/dist/shared';
import { cloneDeep, filter, map } from 'lodash-es';
import { Observable, tap } from 'rxjs';

import { calculatorChartOptions } from '../../../../constants/calculator-chart-options.constants';
import { rateMask } from '../../../../constants/maskito.constants';
import * as fromCalculatorActions from '../../../../store/actions/calculator.actions';
import * as fromCalculatorSelectors from '../../../../store/selectors/calculator.selectors';
import { AffordabilityData } from '../../../../typings/calculator';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-affordability-report-modal',
  standalone: false,
  templateUrl: './report-modal.component.html'
})
export class ReportModalComponent {
  private modalController = inject(ModalController);
  private store = inject(Store);

  selectData$: Observable<AffordabilityData>;
  reportData: AffordabilityData | undefined;

  readonly rateMask: MaskitoOptions = rateMask;

  chartOptions = cloneDeep(calculatorChartOptions);

  constructor() {
    this.selectData$ = this.store.pipe(
      select(fromCalculatorSelectors.selectAffordabilityData),
      tap((data) => {
        const seriesData = [
          ...filter(
            [
              {
                name: 'Mortgage Amount',
                value: data.mortgageAmount
              },
              { name: 'Down Payment', value: data.downPayment }
            ],
            (item) => item.value > 0
          ),
          {
            name: `Term: ${data.mortgageTerm} year${data.mortgageTerm === 1 ? '' : 's'}`
          },
          {
            name: `Rate: ${data.interestRate}%`
          }
        ];
        (this.chartOptions.series as SeriesOption[])[0].data = seriesData;
        (this.chartOptions.legend as LegendOption).data = map(seriesData, (item) => {
          return {
            icon: ['Mortgage Amount', 'Down Payment'].includes(item.name) ? 'rect' : 'none',
            name: item.name
          };
        });
        this.reportData = data;
      })
    );
  }

  close() {
    return this.modalController.dismiss(null, 'cancel');
  }

  async save() {
    if (this.reportData) {
      this.store.dispatch(
        fromCalculatorActions.saveAffordabilityReport({
          payload: {
            reportData: this.reportData
          }
        })
      );
    }

    await this.close();
  }
}
